import React, { useState, useCallback, useMemo } from 'react';
import { generateImage, generateTextContent, generateListicleContent, generateCarouselContent, generateThemeCollageContent, generateTipPinContent, generateMultipleImages, generateTipsCarouselContent, generateSmartCarousel } from '../services/geminiService';
import { AnyPinContent, ColorPalette, Icon, AspectRatio } from '../types';
import { ICONS, COLOR_PALETTES } from '../constants';
import PinPreview from './PinPreview';
import Loader from './ui/Loader';
import { usePinHistory } from '../hooks/usePinHistory';

type PinType = 'standard' | 'listicle' | 'carousel' | 'smart-carousel' | 'theme-collage' | 'tip';

function PinGenerator() {
  const [prompt, setPrompt] = useState<string>('A soft and cozy minimalist living room with natural materials and light.');
  const [selectedIconName, setSelectedIconName] = useState<string>(ICONS[0].name);
  const [selectedPaletteName, setSelectedPaletteName] = useState<string>(COLOR_PALETTES[0].name);
  const [pinContent, setPinContent] = useState<AnyPinContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pinType, setPinType] = useState<PinType>('standard');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [imageAspectRatio, setImageAspectRatio] = useState<AspectRatio>('2:3');
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [useHtmlMode, setUseHtmlMode] = useState<boolean>(false);
  const [useAiImageMode, setUseAiImageMode] = useState<boolean>(false);
  const [aiImagePrompt, setAiImagePrompt] = useState<string>('');
  const [aiImageCount, setAiImageCount] = useState<number>(4);
  const [isGeneratingImages, setIsGeneratingImages] = useState<boolean>(false);
  const [useTipsMode, setUseTipsMode] = useState<boolean>(true);
  const [styleReferenceImage, setStyleReferenceImage] = useState<string | null>(null);
  const [isReferenceDragging, setIsReferenceDragging] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  // Smart Carousel state
  const [smartSlideCount, setSmartSlideCount] = useState<number>(6);
  const [brandingUrl, setBrandingUrl] = useState<string>('');
  const [showBranding, setShowBranding] = useState<boolean>(true);

  const { history, savePin, toggleFavorite, deletePin, getRecent, getFavorites } = usePinHistory();

  // Auto-save pins to history when generated
  const lastSavedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (pinContent && !isLoading) {
      const contentKey = JSON.stringify(pinContent);
      if (lastSavedRef.current !== contentKey) {
        savePin(pinContent);
        lastSavedRef.current = contentKey;
      }
    }
  }, [pinContent, isLoading, savePin]);

  // Custom Brand Style (user-defined via text and/or image)
  const BRAND_STYLE_STORAGE_KEY = 'pin-generator-brand-style';
  const loadSavedBrandStyle = () => {
    try {
      const saved = localStorage.getItem(BRAND_STYLE_STORAGE_KEY);
      return saved ? JSON.parse(saved) : { description: '', referenceImage: null };
    } catch { return { description: '', referenceImage: null }; }
  };
  const [brandStyleEnabled, setBrandStyleEnabled] = useState<boolean>(false);
  const [brandStyleDescription, setBrandStyleDescription] = useState<string>(loadSavedBrandStyle().description);
  const [brandStyleImage, setBrandStyleImage] = useState<string | null>(loadSavedBrandStyle().referenceImage);

  // Save brand style to localStorage when changed
  React.useEffect(() => {
    localStorage.setItem(BRAND_STYLE_STORAGE_KEY, JSON.stringify({
      description: brandStyleDescription,
      referenceImage: brandStyleImage
    }));
  }, [brandStyleDescription, brandStyleImage]);

  const selectedIcon = useMemo(() => ICONS.find(i => i.name === selectedIconName) || ICONS[0], [selectedIconName]);
  const selectedPalette = useMemo(() => COLOR_PALETTES.find(p => p.name === selectedPaletteName) || COLOR_PALETTES[0], [selectedPaletteName]);

  const processFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setError(null);
    const imagePromises = Array.from(files).map(file => {
      return new Promise<string>((resolve, reject) => {
        if (!file.type.startsWith('image/')) {
          reject(new Error(`File "${file.name}" is not an image.`));
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(new Error(`Failed to read file "${file.name}".`));
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(imagePromises)
      .then(base64Images => {
        setUploadedImages(prev => [...prev, ...base64Images]);
      })
      .catch(err => {
        setError(err.message);
      });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(event.target.files);
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  }

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsDragging(false);
    processFiles(event.dataTransfer.files);
  };

  const extractImagesFromHtml = (html: string): string[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgTags = doc.querySelectorAll('img');
    const imageUrls: string[] = [];

    imgTags.forEach(img => {
      const src = img.getAttribute('src');
      if (src && (src.includes('shopify.com') || src.startsWith('http'))) {
        imageUrls.push(src);
      }
    });

    return imageUrls;
  };


  const handleGeneratePin = useCallback(async () => {
    setError(null);

    // Handle HTML mode for carousel FIRST (no prompt or uploads needed)
    if (pinType === 'carousel' && useHtmlMode) {
      if (!htmlContent.trim()) {
        setError('Please paste HTML content.');
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const imgUrls = extractImagesFromHtml(htmlContent);

      if (imgUrls.length === 0) {
        setError('No images found in HTML. Make sure images have src attributes.');
        return;
      }

      const titleFromHtml = doc.querySelector('h1')?.textContent?.trim() || 'Design Principles';
      const plainTextContext = doc.body?.textContent?.replace(/\s+/g, ' ').trim() || '';

      setIsLoading(true);
      setPinContent(null);

      try {
        const generatedData = await generateCarouselContent(
          titleFromHtml,
          imgUrls,
          true,
          plainTextContext
        );

        setPinContent({
          type: 'carousel',
          title: generatedData.title || titleFromHtml,
          userImages: imgUrls,
          imageAspectRatio: imageAspectRatio,
          generated: {
            slideTexts: generatedData.slideTexts,
            slideDescriptions: generatedData.slideDescriptions
          },
        });
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!prompt.trim()) {
      setError('Please enter a prompt.');
      return;
    }

    const requiresUploadedImages =
      (pinType === 'carousel' && !useHtmlMode) || pinType === 'theme-collage' || pinType === 'tip';

    if (requiresUploadedImages && uploadedImages.length === 0) {
      setError(`Please upload at least one image for the ${pinType} type.`);
      return;
    }

    setIsLoading(true);
    setPinContent(null);

    try {
      if (pinType === 'standard') {
        const [imageData, textData] = await Promise.all([
          generateImage(prompt),
          generateTextContent(prompt),
        ]);
        setPinContent({
          type: 'standard',
          image: imageData,
          title: textData.title,
          description: textData.description,
        });
      } else if (pinType === 'listicle') {
        const listicleData = await generateListicleContent(prompt, styleReferenceImage || undefined);
        setPinContent(listicleData);
      } else if (pinType === 'carousel') {
        // Use tips carousel mode by default for styled text overlays
        if (useTipsMode) {
          const generatedData = await generateTipsCarouselContent(prompt, uploadedImages);
          setPinContent({
            type: 'carousel',
            title: generatedData.title,
            userImages: uploadedImages,
            imageAspectRatio: imageAspectRatio,
            generated: {
              slideTexts: generatedData.slideTexts,
              slideDescriptions: generatedData.slideDescriptions,
              slideTips: generatedData.slideTips
            },
          });
        } else {
          const generatedData = await generateCarouselContent(prompt, uploadedImages, false);
          setPinContent({
            type: 'carousel',
            title: generatedData.title,
            userImages: uploadedImages,
            imageAspectRatio: imageAspectRatio,
            generated: {
              slideTexts: generatedData.slideTexts,
              slideDescriptions: generatedData.slideDescriptions
            },
          });
        }
      } else if (pinType === 'theme-collage') {
        const generatedData = await generateThemeCollageContent(prompt);

        const chunkArray = (arr: string[], size: number): string[][] =>
          Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
            arr.slice(i * size, i * size + size)
          );

        const imageGroups = chunkArray(uploadedImages, 4);

        setPinContent({
          type: 'theme-collage',
          userImageGroups: imageGroups,
          imageAspectRatio: imageAspectRatio,
          generated: generatedData
        });
      } else if (pinType === 'tip') {
        const generatedData = await generateTipPinContent(prompt);
        setPinContent({
          type: 'tip',
          userImage: uploadedImages[0],
          imageAspectRatio: imageAspectRatio,
          generated: {
            title: generatedData.title,
            tipText: generatedData.tipText
          },
        });
      } else if (pinType === 'smart-carousel') {
        // Smart Carousel - just needs a topic, generates everything
        const smartData = await generateSmartCarousel(
          prompt,
          smartSlideCount,
          showBranding ? brandingUrl || undefined : undefined
        );
        setPinContent({
          type: 'smart-carousel',
          mainTitle: smartData.mainTitle,
          colorPalette: smartData.colorPalette,
          slides: smartData.slides,
          brandingUrl: showBranding ? brandingUrl || undefined : undefined,
          imageAspectRatio: imageAspectRatio
        });
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [prompt, pinType, uploadedImages, styleReferenceImage, imageAspectRatio, useHtmlMode, htmlContent, useTipsMode, useAiImageMode, aiImagePrompt, aiImageCount, selectedIconName, selectedPaletteName, smartSlideCount, showBranding, brandingUrl]);

  const handleReferenceUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onloadend = () => setStyleReferenceImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleReferenceDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setIsReferenceDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setStyleReferenceImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const PinTypeButton = ({ type, label }: { type: PinType, label: string }) => (
    <button
      onClick={() => {
        setPinType(type);
        setPinContent(null); // Clear preview when changing type
      }}
      className={`w-full text-center py-2.5 rounded-md text-sm font-semibold transition-colors ${pinType === type
        ? 'bg-pink-600 text-white shadow'
        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
        }`}
    >
      {label}
    </button>
  );

  const AspectRatioButton = ({ ratio, label }: { ratio: AspectRatio, label: string }) => (
    <button
      onClick={() => setImageAspectRatio(ratio)}
      className={`w-full text-center py-2.5 rounded-md text-sm font-semibold transition-colors ${imageAspectRatio === ratio
        ? 'bg-pink-600 text-white shadow'
        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
        }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Controls Panel */}
      <div className="w-full lg:w-1/2 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="space-y-6">
          {/* History Toggle */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-800">Pin Generator</h2>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${showHistory
                ? 'bg-purple-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {showHistory ? 'Hide History' : `History (${history.length})`}
            </button>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">Recent Pins</h3>
                <div className="flex gap-2">
                  <span className="text-xs text-slate-500">⭐ {getFavorites().length} favorites</span>
                </div>
              </div>

              {history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No pins in history yet</p>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {history.slice(0, 16).map(pin => (
                    <div
                      key={pin.id}
                      className="relative group cursor-pointer aspect-[2/3] bg-slate-200 rounded-md overflow-hidden"
                      onClick={() => {
                        setPinContent(pin.content);
                        setShowHistory(false);
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-1">
                        <span className="text-white text-[10px] font-semibold capitalize">{pin.content.type}</span>
                      </div>
                      {pin.isFavorite && (
                        <span className="absolute top-1 right-1 text-yellow-400 text-xs">⭐</span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(pin.id); }}
                        className="absolute top-1 left-1 bg-white/80 rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                      >
                        {pin.isFavorite ? '★' : '☆'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deletePin(pin.id); }}
                        className="absolute bottom-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">1. Select Pin Type</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <PinTypeButton type="standard" label="Standard" />
              <PinTypeButton type="listicle" label="Listicle" />
              <PinTypeButton type="carousel" label="Carousel" />
              <PinTypeButton type="theme-collage" label="Theme Collage" />
              <PinTypeButton type="tip" label="Tip" />
              <button
                disabled
                className="w-full text-center py-2.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-400 cursor-not-allowed border border-dashed border-slate-300"
              >
                ✨ Smart Carousel
                <span className="block text-xs font-normal">Coming Soon</span>
              </button>
            </div>
          </div>

          {/* Brand Style - Custom Input with Toggle */}
          <div className={`p-4 rounded-xl border transition-all ${brandStyleEnabled
            ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300'
            : 'bg-slate-50 border-slate-200'
            }`}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                🎨 Brand Style Consistency
              </label>
              <button
                onClick={() => setBrandStyleEnabled(!brandStyleEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${brandStyleEnabled ? 'bg-purple-600' : 'bg-slate-300'
                  }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${brandStyleEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
              </button>
            </div>

            {brandStyleEnabled && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Define your brand style to apply across all generated pins.</p>

                <div>
                  <label className="text-xs font-medium text-slate-600">Brand Description</label>
                  <textarea
                    value={brandStyleDescription}
                    onChange={(e) => setBrandStyleDescription(e.target.value)}
                    placeholder="Describe your brand style: colors, fonts, mood, tone...&#10;&#10;e.g., Warm, earthy tones with cream and terracotta. Serif fonts. Cozy, inviting feel. Natural textures."
                    className="mt-1 block w-full bg-white border border-slate-200 rounded-lg text-sm p-3 h-20 resize-none text-slate-900 placeholder-slate-400"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-600">Reference Image (Optional)</label>
                  {!brandStyleImage ? (
                    <label className="mt-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                      <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mx-auto h-6 w-6 text-slate-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25z" />
                        </svg>
                        <p className="mt-1 text-xs text-slate-500">Upload brand reference</p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => setBrandStyleImage(reader.result as string);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  ) : (
                    <div className="mt-1 relative group">
                      <img src={brandStyleImage} alt="Brand reference" className="w-full h-20 object-cover rounded-lg" />
                      <button
                        onClick={() => setBrandStyleImage(null)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!(pinType === 'carousel' && useHtmlMode) && (
            <div>
              <label htmlFor="prompt" className="text-sm font-medium text-gray-700">
                2. {pinType === 'smart-carousel' ? 'Enter your topic' : 'Describe your pin'}
              </label>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  pinType === 'smart-carousel'
                    ? "e.g., 5 Ways to Make a Small Room Feel Bigger"
                    : pinType === 'carousel'
                      ? "e.g., 5 ways to style a small balcony"
                      : pinType === 'tip'
                        ? "e.g., Use large mirrors to make a small room feel bigger"
                        : "e.g., healthy vegan breakfast ideas"
                }
                className="mt-2 block w-full bg-slate-50 border border-slate-300 rounded-lg shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm p-3 h-24 resize-none text-slate-900 placeholder-slate-400"
              />
            </div>
          )}

          {/* Smart Carousel Options - Simple and Clean */}
          {pinType === 'smart-carousel' && (
            <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">✨</span>
                <h3 className="text-sm font-semibold text-purple-900">Smart Carousel Settings</h3>
              </div>

              {/* Slide Count */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Number of Slides</label>
                <div className="grid grid-cols-4 gap-2">
                  {[4, 5, 6, 7, 8].map(num => (
                    <button
                      key={num}
                      onClick={() => setSmartSlideCount(num)}
                      className={`py-2 rounded-md text-sm font-semibold transition-colors ${smartSlideCount === num
                        ? 'bg-purple-600 text-white shadow'
                        : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                        }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">1 hook + {smartSlideCount - 2} tips + 1 CTA</p>
              </div>

              {/* Branding Toggle and URL */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-600">Website Branding</label>
                  <button
                    onClick={() => setShowBranding(!showBranding)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showBranding ? 'bg-purple-600' : 'bg-slate-300'}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${showBranding ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                </div>
                {showBranding && (
                  <input
                    type="text"
                    value={brandingUrl}
                    onChange={(e) => setBrandingUrl(e.target.value)}
                    placeholder="yourwebsite.com"
                    className="w-full bg-white border border-slate-200 rounded-lg text-sm p-2 text-slate-900 placeholder-slate-400"
                  />
                )}
              </div>

              <p className="text-xs text-purple-700 bg-purple-100 p-2 rounded-lg">
                💡 Just enter a topic like "5 Ways to Make a Small Room Feel Bigger" and we'll create the hook, tips, images, and CTA slides automatically!
              </p>
            </div>
          )}

          {pinType === 'listicle' && (
            <div>
              <label className="text-sm font-medium text-gray-700">3. Style Reference (Optional)</label>
              <p className="text-xs text-slate-500 mb-2">Upload an image to define the colors, fonts, and layout style.</p>

              {!styleReferenceImage ? (
                <div className="mt-2">
                  <label
                    htmlFor="reference-upload"
                    className={`w-full flex items-center justify-center px-4 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isReferenceDragging
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-300 hover:bg-slate-100 hover:border-slate-400'
                      }`}
                    onDragOver={(e) => { e.preventDefault(); setIsReferenceDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsReferenceDragging(false); }}
                    onDrop={handleReferenceDrop}
                  >
                    <div className="text-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mx-auto h-8 w-8 text-indigo-400 mb-2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.077-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.048 4.025a3 3 0 01-4.293 0l-1.414-1.414a3.375 3.375 0 01-.17-4.473m17.546 9.882a6.321 6.321 0 01-1.128-.22c-1.372-.636-2.246-2.056-2.246-3.762 0-3.479 2.652-5.71 6.33-6.177A18.8 18.8 0 0019.5 9 18.8 18.8 0 0012 1.5M12 10.5h.008v.008H12V10.5z" />
                      </svg>
                      <p className="text-sm font-medium text-slate-700">Drop style reference</p>
                      <p className="text-xs text-slate-500">The AI will mimic this style</p>
                    </div>
                  </label>
                  <input type="file" id="reference-upload" accept="image/*" className="hidden" onChange={handleReferenceUpload} />
                </div>
              ) : (
                <div className="mt-2 relative group rounded-lg overflow-hidden border border-slate-200">
                  <div className="aspect-[3/1] w-full bg-slate-100 relative">
                    <img src={styleReferenceImage} alt="Style Reference" className="w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <span className="text-white text-xs font-semibold px-2 py-1 bg-black/50 rounded">Style Locked</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setStyleReferenceImage(null)}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-700 shadow-sm"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          )}

          {pinType === 'standard' && (
            <>
              {/* IconSelector and ColorPaletteSelector are hidden for standard pin */}
            </>
          )}

          {(pinType === 'carousel' || pinType === 'theme-collage' || pinType === 'tip') && (
            <>
              <div>
                <label className="text-sm font-medium text-gray-700">3. Choose Input Method</label>
                <div className="mt-2 grid grid-cols-3 gap-3">
                  <button
                    onClick={() => { setUseHtmlMode(false); setUseAiImageMode(false); }}
                    className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${!useHtmlMode && !useAiImageMode
                      ? 'bg-pink-600 text-white shadow'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                  >
                    Upload Images
                  </button>
                  {pinType === 'carousel' && (
                    <button
                      onClick={() => { setUseHtmlMode(true); setUseAiImageMode(false); }}
                      className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${useHtmlMode
                        ? 'bg-pink-600 text-white shadow'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                        }`}
                    >
                      Paste HTML
                    </button>
                  )}
                  <button
                    onClick={() => { setUseAiImageMode(true); setUseHtmlMode(false); }}
                    className={`py-2.5 rounded-md text-sm font-semibold transition-colors ${useAiImageMode
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      }`}
                  >
                    ✨ Generate with AI
                  </button>
                </div>
              </div>

              {/* Tips Mode Toggle - Carousel Only */}
              {pinType === 'carousel' && (
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-lime-50 to-emerald-50 rounded-lg border border-lime-200">
                  <button
                    onClick={() => setUseTipsMode(!useTipsMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${useTipsMode ? 'bg-lime-500' : 'bg-slate-300'
                      }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${useTipsMode ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                  </button>
                  <div>
                    <span className="text-sm font-semibold text-slate-800">Tips Carousel Mode</span>
                    <p className="text-xs text-slate-500">Styled text overlays with accent titles & body text</p>
                  </div>
                </div>
              )}

              {useAiImageMode ? (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700">4. Describe the Images to Generate</label>
                    <textarea
                      value={aiImagePrompt}
                      onChange={(e) => setAiImagePrompt(e.target.value)}
                      placeholder="Describe the images you want AI to create...&#10;&#10;e.g., Modern minimalist living room with natural light, cozy textiles, indoor plants, Scandinavian design aesthetic"
                      className="mt-2 block w-full bg-slate-50 border border-slate-300 rounded-lg shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm p-3 h-28 resize-none text-slate-900 placeholder-slate-400"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      AI will generate {aiImageCount} unique image variations based on your description
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">5. Number of Images</label>
                    <div className="mt-2 grid grid-cols-4 sm:grid-cols-8 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                        <button
                          key={num}
                          onClick={() => setAiImageCount(num)}
                          className={`py-2 rounded-md text-sm font-semibold transition-colors ${aiImageCount === num
                            ? 'bg-purple-600 text-white shadow'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                            }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  {uploadedImages.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Generated Images Preview</label>
                      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {uploadedImages.map((imgSrc, i) => (
                          <div key={i} className="relative aspect-square group">
                            <img src={imgSrc} alt={`AI generated ${i}`} className="w-full h-full object-cover rounded-md" />
                            <button onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (!aiImagePrompt.trim()) {
                        setError('Please describe the images you want to generate');
                        return;
                      }
                      setError(null);
                      setIsGeneratingImages(true);
                      try {
                        const generatedImages = await generateMultipleImages(aiImagePrompt, aiImageCount, imageAspectRatio);
                        setUploadedImages(generatedImages);
                      } catch (err: any) {
                        setError(err.message || 'Failed to generate images');
                      } finally {
                        setIsGeneratingImages(false);
                      }
                    }}
                    disabled={isGeneratingImages || !aiImagePrompt.trim()}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-2.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    {isGeneratingImages ? (
                      <>
                        <Loader />
                        <span>Generating {aiImageCount} Images...</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                        </svg>
                        <span>Generate Images with AI</span>
                      </>
                    )}
                  </button>
                </>
              ) : useHtmlMode && pinType === 'carousel' ? (
                <div>
                  <label className="text-sm font-medium text-gray-700">4. Paste HTML Content</label>
                  <textarea
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    placeholder="Paste your HTML blog content here (with Shopify image URLs)..."
                    className="mt-2 block w-full bg-slate-50 border border-slate-300 rounded-lg shadow-sm focus:ring-pink-500 focus:border-pink-500 sm:text-sm p-3 h-32 resize-none text-slate-900 placeholder-slate-400 font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    The system will extract images from &lt;img&gt; tags and create brief concept descriptions (max 8 words each).
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-700">4. Upload Your Images</label>
                  <div className="mt-2">
                    <label
                      htmlFor="image-upload"
                      className={`w-full flex items-center justify-center px-4 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDragging
                        ? 'border-pink-500 bg-pink-500/10'
                        : 'border-slate-300 hover:bg-slate-100 hover:border-slate-400'
                        }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mx-auto h-8 w-8 text-slate-400"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
                        <p className="mt-2 text-sm text-slate-600">Click to upload or drag & drop</p>
                        <p className="text-xs text-slate-400">PNG, JPG, WEBP</p>
                      </div>
                    </label>
                    <input type="file" id="image-upload" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
                  </div>
                  {uploadedImages.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                      {uploadedImages.map((imgSrc, i) => (
                        <div key={i} className="relative aspect-square group">
                          <img src={imgSrc} alt={`upload preview ${i}`} className="w-full h-full object-cover rounded-md" />
                          <button onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">{useAiImageMode ? '6' : useHtmlMode ? '5' : '5'}. Image Aspect Ratio</label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <AspectRatioButton ratio="1:1" label="Square (1:1)" />
                  <AspectRatioButton ratio="2:3" label="Vertical (2:3)" />
                  <AspectRatioButton ratio="4:5" label="Portrait (4:5)" />
                  <AspectRatioButton ratio="4:3" label="Landscape (4:3)" />
                </div>
              </div>
            </>
          )}


          {error && <div className="bg-red-100 text-red-700 border border-red-200 p-3 rounded-lg text-sm">{error}</div>}

          <button
            onClick={handleGeneratePin}
            disabled={isLoading || isGeneratingImages || (
              (pinType === 'carousel' && useHtmlMode && !htmlContent.trim()) ||
              (pinType === 'carousel' && !useHtmlMode && !useAiImageMode && uploadedImages.length === 0) ||
              (pinType === 'carousel' && useAiImageMode && uploadedImages.length === 0) ||
              ((pinType === 'theme-collage' || pinType === 'tip') && !useAiImageMode && uploadedImages.length === 0) ||
              ((pinType === 'theme-collage' || pinType === 'tip') && useAiImageMode && uploadedImages.length === 0)
            )}
            className="w-full bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-fuchsia-300 disabled:to-pink-300 text-white font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            {isLoading ? (
              <>
                <Loader />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.898 20.553L16.5 21.75l-.398-1.197a3.375 3.375 0 00-2.456-2.456L12.75 18l1.197-.398a3.375 3.375 0 002.456-2.456L16.5 14.25l.398 1.197a3.375 3.375 0 002.456 2.456L20.25 18l-1.197.398a3.375 3.375 0 00-2.456 2.456z" />
                </svg>
                <span>Generate Pin</span>
              </>
            )}
          </button>
        </div>
      </div>
      <PinPreview
        isLoading={isLoading}
        content={pinContent}
        selectedIcon={selectedIcon}
        selectedPalette={selectedPalette}
      />
    </div >
  );
}

export default PinGenerator;